import type { PlanningPreviewSelectOption } from "../_shared/_logic/table-contract"

type EmployeeShortLabelEntry = {
  value: string
  label: string
}

type EmployeeNameParts = EmployeeShortLabelEntry & {
  first: string[]
  last: string[]
  baseCandidate: string
}

export type EmployeeShortLabelPresentation = {
  shortLabel: string
  tooltipLabel: string
  ariaLabel: string
}

const normalizeLabel = (value: string): string => value.trim().replace(/\s+/g, " ")

const normalizeToken = (value: string): string => value.replace(/[^\p{L}\p{N}]/gu, "")

const upper = (value: string): string => value.toLocaleUpperCase("de-DE")

const chars = (value: string): string[] => Array.from(normalizeToken(value))

const nameParts = (entry: EmployeeShortLabelEntry): EmployeeNameParts => {
  const label = normalizeLabel(entry.label) || "Mitarbeiter"
  const tokens = label.split(" ").map(normalizeToken).filter(Boolean)
  const first = chars(tokens[0] ?? label)
  const last = chars(tokens.length > 1 ? (tokens[tokens.length - 1] ?? "") : "")
  const baseCandidate = upper(`${first[0] ?? ""}${last[0] ?? ""}`) || "M"

  return {
    value: String(entry.value),
    label,
    first,
    last,
    baseCandidate,
  }
}

const candidate = (entry: EmployeeNameParts, firstLength: number, lastLength: number): string =>
  upper(
    `${entry.first.slice(0, firstLength).join("")}${entry.last.slice(0, lastLength).join("")}`,
  ) || "M"

const duplicateGroups = (labels: string[]): number[][] => {
  const byLabel = new Map<string, number[]>()
  labels.forEach((label, index) => {
    const current = byLabel.get(label) ?? []
    current.push(index)
    byLabel.set(label, current)
  })

  return Array.from(byLabel.values()).filter((indexes) => indexes.length > 1)
}

const uniqueEntries = (options: PlanningPreviewSelectOption[]): EmployeeShortLabelEntry[] => {
  const seen = new Set<string>()
  const entries: EmployeeShortLabelEntry[] = []
  for (const option of options) {
    const value = String(option.value ?? "").trim()

    if (!value || seen.has(value)) continue

    seen.add(value)
    entries.push({
      value,
      label: normalizeLabel(option.label) || "Mitarbeiter",
    })
  }

  return entries
}

export const buildEmployeeShortLabelPresentations = (
  options: PlanningPreviewSelectOption[],
): Record<string, EmployeeShortLabelPresentation> => {
  const entries = uniqueEntries(options).map(nameParts)
  const lengths = entries.map((entry) => ({
    first: Math.min(1, entry.first.length),
    last: entry.last.length ? 1 : 0,
  }))

  for (let guard = 0; guard < 64; guard += 1) {
    const labels = entries.map((entry, index) =>
      candidate(entry, lengths[index].first, lengths[index].last),
    )
    const duplicates = duplicateGroups(labels)

    if (!duplicates.length) break

    let changed = false
    for (const indexes of duplicates) {
      for (const index of indexes) {
        const entry = entries[index]
        const length = lengths[index]

        if (length.first < entry.first.length) {
          length.first += 1
          changed = true
        } else if (length.last < entry.last.length) {
          length.last += 1
          changed = true
        }
      }
    }

    if (!changed) break
  }

  const labels = entries.map((entry, index) =>
    candidate(entry, lengths[index].first, lengths[index].last),
  )
  for (const indexes of duplicateGroups(labels)) {
    indexes.forEach((entryIndex, duplicateIndex) => {
      labels[entryIndex] = `${entries[entryIndex].baseCandidate}${duplicateIndex + 1}`
    })
  }

  return Object.fromEntries(
    entries.map((entry, index) => [
      entry.value,
      {
        shortLabel: labels[index],
        tooltipLabel: entry.label,
        ariaLabel: entry.label,
      },
    ]),
  )
}

const fallbackPresentation = (
  option: PlanningPreviewSelectOption,
): EmployeeShortLabelPresentation => {
  const entry = nameParts({
    value: String(option.value ?? ""),
    label: normalizeLabel(option.label) || "Mitarbeiter",
  })

  return {
    shortLabel: entry.baseCandidate,
    tooltipLabel: entry.label,
    ariaLabel: entry.label,
  }
}

export const decorateEmployeeOptionsWithShortLabels = (
  options: PlanningPreviewSelectOption[],
  presentations: Record<string, EmployeeShortLabelPresentation>,
): PlanningPreviewSelectOption[] =>
  options.map((option) => {
    const presentation = presentations[String(option.value)] ?? fallbackPresentation(option)

    return {
      ...option,
      shortLabel: presentation.shortLabel,
      tooltipLabel: presentation.tooltipLabel,
      ariaLabel: presentation.ariaLabel,
    }
  })
